import {useMemo, useState} from "react";
import {SectionDiff} from "../../utils/branchDiff";
import Collapsible from "../../elements/collapsible";
import RecordDiffView from "./recordDiffView";
import {RemoteConstantsModel} from "../../models/remoteConstants.model";
import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import {classifyRecordMigration, classifyRecordRemoval, classifyRecordUpdate, findRecordByIdentity} from "../../utils/migrateRecord";
import {MigrateRecordCommand} from "../../commands/migrateRecord.command";
import {UpdateRecordCommand} from "../../commands/updateRecord.command";
import {RemoveRecordCommand} from "../../commands/removeRecord.command";
import {MigrateWithDependenciesCommand} from "../../commands/migrateWithDependencies.command";
import {BatchCommand} from "../../commands/batch.command";
import {planRecordMigration} from "../../utils/migratePlan";
import {isRecordSection, RecordSectionKey} from "../../utils/branchDiff";
import {SectionActionSummary} from "./sectionActions";
import ActionButton from "./actionButton";
import GlobalsSectionView from "./globalsSectionView";
import MapSectionView from "./mapSectionView";

interface SectionDiffViewProps {
    section: SectionDiff;
    targetData: RemoteConstantsModel;
    actionSummary: SectionActionSummary;
    open: boolean;
    onOpenChange(open: boolean): void;
}

function SectionDiffView(props: SectionDiffViewProps) {
    const s = props.section;
    const {executeCommand} = useIntegrator();
    const {ready, blocked, bulkCommands} = props.actionSummary;

    const counts = (
        <span style={{display: 'inline-flex', gap: 10, alignItems: 'center', fontSize: 13}}>
            {s.onlyInTarget.length > 0 && <span style={{color: '#7ad17a'}}>+{s.onlyInTarget.length}</span>}
            {s.onlyInCurrent.length > 0 && <span style={{color: '#ff8888'}}>-{s.onlyInCurrent.length}</span>}
            {s.differs.length > 0 && <span style={{color: 'white'}}>Δ{s.differs.length}</span>}
            {blocked > 0 && <span style={{color: '#ffaa66'}} title={`${blocked} change(s) blocked by missing dependencies or references`}>{blocked} blocked</span>}
        </span>
    );

    const header = (
        <div className={'layout horizontal center stretch'} style={{width: '100%', gap: 10}}>
            <p style={{fontWeight: 'bold'}}>{s.title}</p>
            <span style={{display: 'inline-flex', gap: 10, alignItems: 'center'}}>
                {s.identical ? <span style={{color: 'var(--muted-text-color)', fontSize: 13}}>identical</span> : counts}
                {ready > 0 && (
                    <ActionButton variant={'pull'} label={`Take all (${ready})`}
                                  title={`Apply all ${ready} resolvable change(s) in "${s.title}" as one undo step`}
                                  onClick={() => executeCommand(new BatchCommand(bulkCommands))}/>
                )}
            </span>
        </div>
    );

    if (s.identical) {
        return (
            <div className={'box'} style={{padding: '6px 12px', opacity: 0.6}}>
                {header}
            </div>
        );
    }

    return (
        <div className={'box'} style={{padding: '6px 12px'}}>
            <Collapsible title={header} open={props.open} onOpenChange={props.onOpenChange}>
                {s.flavor === 'globals' && <GlobalsSectionView section={s}/>}
                {s.flavor === 'map' && <MapSectionView section={s} targetData={props.targetData}/>}
                {s.flavor === 'record' && (
                    <div className={'layout vertical gap'} style={{gap: 8, marginTop: 8}}>
                        {s.onlyInTarget.length > 0 && (
                            <div className={'layout vertical'}>
                                <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 13}}>
                                    Only in Other ({s.onlyInTarget.length})
                                </p>
                                {s.onlyInTarget.map(r => (
                                    <OnlyInOtherRow key={r.identity}
                                                    sectionKey={s.sectionKey}
                                                    identity={r.identity}
                                                    name={r.name}
                                                    targetData={props.targetData}/>
                                ))}
                            </div>
                        )}
                        {s.onlyInCurrent.length > 0 && (
                            <div className={'layout vertical'}>
                                <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 13}}>
                                    Only in Current ({s.onlyInCurrent.length})
                                </p>
                                {s.onlyInCurrent.map(r => (
                                    <OnlyInCurrentRow key={r.identity}
                                                      sectionKey={s.sectionKey as RecordSectionKey}
                                                      identity={r.identity}
                                                      name={r.name}/>
                                ))}
                            </div>
                        )}
                        {s.differs.length > 0 && (
                            <div className={'layout vertical'}>
                                <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 13}}>
                                    Differs ({s.differs.length})
                                </p>
                                {s.differs.map(r => <DiffersRow key={r.identity} identity={r.identity} name={r.name}
                                                                fieldChanges={r.fieldChanges} identityHint={r.identityHint}
                                                                sectionKey={s.sectionKey} targetData={props.targetData}/>)}
                            </div>
                        )}
                    </div>
                )}
            </Collapsible>
        </div>
    );
}

// Row in the "Only in Current" list (record sections). The record exists only
// on Current; the trash button removes it to match Other, but only when no
// other record on Current still references it (classifyRecordRemoval).
function OnlyInCurrentRow(props: {
    sectionKey: RecordSectionKey;
    identity: string;
    name: string;
}) {
    const {state, executeCommand} = useIntegrator();
    const {getLocalizationValue} = useApp();

    const classification = useMemo(() => {
        if (!isRecordSection(props.sectionKey)) return undefined;
        const record = findRecordByIdentity(state, props.sectionKey, props.identity);
        if (!record) return undefined;
        return classifyRecordRemoval(record, props.sectionKey, state, getLocalizationValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.identity, props.sectionKey, state]);

    return (
        <div className={'layout horizontal'} style={{paddingLeft: 10, alignItems: 'center', gap: 8}}>
            <p style={{flexGrow: 1}}>- {props.name}</p>
            {classification && (
                <ActionButton
                    variant={'remove'}
                    label={'Remove'}
                    disabled={!classification.removable}
                    title={classification.removable
                        ? `Remove "${props.name}" from Current to match Other`
                        : `Can't remove yet — still referenced by:\n${(classification.blockers ?? []).join('\n')}`}
                    onClick={() => executeCommand(new RemoveRecordCommand(props.sectionKey, props.identity))}/>
            )}
        </div>
    );
}

// Row in the "Differs" list. Both branches have the record (same uid) but
// the fields diverge. Expand for a read-only field diff; the sync button
// replaces Current's version (record + localization + icon pack) with
// Other's, for metadata sections.
function DiffersRow(props: {
    identity: string;
    name: string;
    fieldChanges: import('../../utils/branchDiff').RecordDiff['fieldChanges'];
    identityHint?: string;
    sectionKey: import('../../utils/branchDiff').SectionKey;
    targetData: RemoteConstantsModel;
}) {
    const [open, setOpen] = useState(false);
    const {state, executeCommand} = useIntegrator();
    const {getLocalizationValue} = useApp();

    const classification = useMemo(() => {
        const targetRecord = findRecordByIdentity(props.targetData, props.sectionKey, props.identity);
        if (!targetRecord) return undefined;
        return classifyRecordUpdate(targetRecord, props.sectionKey, props.targetData, state, getLocalizationValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.identity, props.sectionKey, props.targetData, state]);

    return (
        <div className={'layout vertical'} style={{paddingLeft: 10}}>
            <div className={'layout horizontal gap'}
                 style={{userSelect: 'none', alignItems: 'center'}}>
                <div className={'layout horizontal gap'}
                     style={{cursor: 'pointer', alignItems: 'center', flexGrow: 1}}
                     onClick={() => setOpen(o => !o)}>
                    <span className={`fa-solid ${open ? 'fa-chevron-down' : 'fa-chevron-right'}`}
                          style={{fontSize: 12, width: 12}}/>
                    <p>Δ {props.name}</p>
                    <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 12}}>
                        ({props.fieldChanges.length} field{props.fieldChanges.length === 1 ? '' : 's'})
                    </p>
                </div>
                {classification && (
                    <ActionButton
                        variant={'pull'}
                        label={'Sync'}
                        disabled={!classification.resolvable}
                        title={classification.resolvable
                            ? `Sync "${props.name}" from Other — replaces Current's record, localization and image pack with Other's version`
                              + ((classification.notes ?? []).map(n => `\n⚠ ${n}`).join(''))
                            : `Can't sync yet:\n${(classification.missingDeps ?? []).join('\n')}`}
                        onClick={() => classification.migration && executeCommand(new UpdateRecordCommand(classification.migration))}/>
                )}
            </div>
            {props.identityHint && (
                <p style={{color: '#ffaa66', fontSize: 12, fontStyle: 'italic', paddingLeft: 22}}>
                    ⚠ {props.identityHint}
                </p>
            )}
            {open && <RecordDiffView fieldChanges={props.fieldChanges} sectionKey={props.sectionKey} identity={props.identity}/>}
        </div>
    );
}

// Row in the "Only in Other" list. Renders the record name + a small
// Resolve button that migrates the record (and its loc / image-pack
// satellites) from Other into Current.
function OnlyInOtherRow(props: {
    sectionKey: import('../../utils/branchDiff').SectionKey;
    identity: string;
    name: string;
    targetData: RemoteConstantsModel;
}) {
    const {state, executeCommand, branch, getBranchVersionNumber} = useIntegrator();
    const {getLocalizationValue} = useApp();

    const currentBranchLabel = `${branch} v${getBranchVersionNumber(branch)}`;

    const classification = useMemo(() => {
        const targetRecord = findRecordByIdentity(props.targetData, props.sectionKey, props.identity);
        if (!targetRecord) return undefined;
        return classifyRecordMigration(targetRecord, props.sectionKey, props.targetData, state, getLocalizationValue, currentBranchLabel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.identity, props.sectionKey, props.targetData, state, currentBranchLabel]);

    // When the direct migrate is blocked by missing dependencies, compute a
    // dependency-ordered plan that pulls them across too — offered as a second
    // button, enabled only when every dependency is resolvable from Other.
    const plan = useMemo(() => {
        if (!classification || classification.resolvable) return undefined;
        if (!isRecordSection(props.sectionKey)) return undefined;
        const targetRecord = findRecordByIdentity(props.targetData, props.sectionKey, props.identity);
        if (!targetRecord) return undefined;
        return planRecordMigration(targetRecord, props.sectionKey, props.targetData, state);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classification, props.identity, props.sectionKey, props.targetData, state]);

    const canCascade = !!plan && plan.unresolved.length === 0 && plan.migrations.length > 0;

    return (
        <div className={'layout horizontal'} style={{paddingLeft: 10, alignItems: 'center', gap: 8}}>
            <p style={{flexGrow: 1}}>+ {props.name}</p>
            {plan && plan.migrations.length > 0 && (
                <ActionButton
                    variant={'pull'}
                    icon={'fa-sitemap'}
                    label={`Pull + ${plan.migrations.length - 1} deps`}
                    disabled={!canCascade}
                    title={canCascade
                        ? `Migrate "${props.name}" together with ${plan.migrations.length - 1} dependenc${plan.migrations.length - 1 === 1 ? 'y' : 'ies'}:\n`
                          + plan.migrations.map(m => `• ${m.identity}`).join('\n')
                        : `Can't cascade — these references don't exist on Other either:\n`
                          + plan.unresolved.map(u => `• ${u.ref} (needed by ${u.neededBy})`).join('\n')}
                    onClick={() => canCascade && executeCommand(new MigrateWithDependenciesCommand(plan.migrations))}/>
            )}
            {classification && (
                <ActionButton
                    variant={'pull'}
                    label={'Pull'}
                    disabled={!classification.resolvable}
                    title={classification.resolvable
                        ? `Migrate "${props.name}" (and its localization + image pack) from Other into Current`
                          + ((classification.notes ?? []).map(n => `\n⚠ ${n}`).join(''))
                        : `Can't migrate yet:\n${(classification.missingDeps ?? []).join('\n')}`}
                    onClick={() => classification.migration && executeCommand(new MigrateRecordCommand(classification.migration))}/>
            )}
        </div>
    );
}

export default SectionDiffView;
